// ─────────────────────────────────────────────────────────────────────────────
// client/src/stores/themeStore.ts
//
// Zustand store for per-account white-label theming + light/dark mode.
// Persisted to localStorage so the theme loads instantly on refresh.
// ─────────────────────────────────────────────────────────────────────────────

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ColorMode = 'dark' | 'light';

export interface AccountTheme {
  accountId: number;
  accentColor: string;
  companyName: string;
}

const DEFAULT_THEME: AccountTheme = {
  accountId: 0,
  accentColor: '#1877f2',
  companyName: 'ENERLYZE',
};

// ── Light mode overrides ─────────────────────────────────────────────────────
// Only the tokens that change between modes. Everything else stays the same.
const DARK_TOKENS = {
  '--color-bg': '#121218',
  '--color-sidebar': '#0c0d12',
  '--color-glass-bg': 'rgba(255, 255, 255, 0.04)',
  '--color-glass-border': 'rgba(255, 255, 255, 0.08)',
  '--color-muted': '#7a8599',
  '--color-text': '#e8ecf2',
};

const LIGHT_TOKENS = {
  '--color-bg': '#f5f7fa',
  '--color-sidebar': '#ffffff',
  '--color-glass-bg': 'rgba(0, 0, 0, 0.03)',
  '--color-glass-border': 'rgba(0, 0, 0, 0.08)',
  '--color-muted': '#64748b',
  '--color-text': '#1e293b',
};

interface ThemeState {
  theme: AccountTheme;
  colorMode: ColorMode;
  isLoaded: boolean;

  setTheme: (theme: AccountTheme) => void;
  resetTheme: () => void;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: DEFAULT_THEME,
      colorMode: 'dark' as ColorMode,
      isLoaded: false,

      setTheme: (theme) => {
        set({ theme, isLoaded: true });
        applyThemeToDom(theme, get().colorMode);
      },

      resetTheme: () => {
        set({ theme: DEFAULT_THEME, isLoaded: false });
        applyThemeToDom(DEFAULT_THEME, get().colorMode);
      },

      setColorMode: (mode) => {
        set({ colorMode: mode });
        applyThemeToDom(get().theme, mode);
      },

      toggleColorMode: () => {
        const next = get().colorMode === 'dark' ? 'light' : 'dark';
        set({ colorMode: next });
        applyThemeToDom(get().theme, next);
      },
    }),
    {
      name: 'enerlyze-theme',
      partialize: (state) => ({
        theme: state.theme,
        colorMode: state.colorMode,
        isLoaded: state.isLoaded,
      }),
      onRehydrateStorage: () => {
        return (rehydratedState) => {
          if (rehydratedState) {
            applyThemeToDom(
              rehydratedState.theme ?? DEFAULT_THEME,
              rehydratedState.colorMode ?? 'dark',
            );
          }
        };
      },
    },
  ),
);

/**
 * Apply theme as CSS custom properties on :root.
 * Sets accent color + all mode-specific tokens.
 */
function applyThemeToDom(theme: AccountTheme, mode: ColorMode) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', theme.accentColor);

  const tokens = mode === 'light' ? LIGHT_TOKENS : DARK_TOKENS;
  for (const [key, value] of Object.entries(tokens)) {
    root.style.setProperty(key, value);
  }

  // Toggle class for any CSS that needs mode-aware selectors
  root.classList.toggle('light', mode === 'light');
  root.classList.toggle('dark', mode === 'dark');
}
